import {IAutoOrderOrderItem} from './auto-order-order';
import {StockItem} from '../../../shared-utilities/models-old/datastructures';

//#region TYPE AutoOrderDefaultVisibleFieldTypeKey

/**
 * Grid-only column fields shown in the open order UI but not stored on order/stock row data.
 */
type AutoOrderGridOnlyVisibleField = 'unitsMonthAvg';

/**
 * ### AutoOrderDefaultVisibleFieldTypeKey
 * Represents the union of valid field keys from both `IAutoOrderOrderItem` and `StockItem`
 * that are eligible to be shown in the Auto Order UI.
 *
 * @interface AutoOrderDefaultVisibleFieldTypeKey
 * @extends Object
 * @defaults N/A
 * @description Used to constrain visible field options to only the keys present in the two relevant data structures.
 *              This ensures consistency and prevents invalid field references in the application’s table or display logic.
 *
 * @example
 * const field: AutoOrderDefaultVisibleFieldTypeKey = 'onHand';
 */
export type AutoOrderDefaultVisibleFieldTypeKey =
  keyof IAutoOrderOrderItem | keyof StockItem | AutoOrderGridOnlyVisibleField;

//#endregion

//#region CONST AutoOrderVisibleFields

/**
 * ### AutoOrderVisibleFields
 * Immutable array of default visible field keys for the Auto Order table UI.
 *
 * @const AutoOrderVisibleFields
 * @readonly
 * @type {readonly AutoOrderDefaultVisibleFieldTypeKey[]}
 * @defaults {@link AutoOrderDefaultVisibleFieldTypeKey}
 * @description Internal constant that defines the default field layout. This array is immutable and type-safe.
 *              It is used as a source of truth for external components that render the Auto Order table.
 *              All fields must exist on either `IAutoOrderOrderItem` or `StockItem`.
 *
 * @example
 * AutoOrderVisibleFields.includes('desc'); // true
 */
export const AutoOrderVisibleFields: readonly AutoOrderDefaultVisibleFieldTypeKey[] = [
  'code',
  'desc',
  'unitsYear',
  'unitsMonthAvg',
  'maxSold',
  'netSold',
  'purchaseOrder',
  'onHand',
  'qtyDiff',
];

/**
 * Canonical open-order grid column order (locked left, default visible, optional, pinned right).
 */
export const AUTO_ORDER_DEFAULT_COLUMN_ORDER: readonly string[] = [
  'icons',
  'code',
  'desc',
  'unitsYear',
  'unitsMonthAvg',
  'maxSold',
  'netSold',
  'purchaseOrder',
  'onHand',
  'qtyDiff',
  'suppCode',
  'dlvColl',
  'regularSuppCode',
  'packSize',
  'maxReturned',
  'ordLvl',
  'gp1',
  'sellPriIncl1',
  'created',
  'lastSold',
  'latestCost',
  'onHoldCode',
  'lineColourCode',
  'mainSupplier',
  'outOfStock',
  'negativeDays',
  'zeroDays',
  'priceEdits',
  'orderQuantity',
  'orderQty',
  'lastStockTake',
  'modified',
  'onHold',
];

//#endregion

//#region CONST AutoOrderDefaultVisibleFields

/**
 * ### AutoOrderDefaultVisibleFields
 * Exported mutable string array derived from the readonly `AutoOrderVisibleFields` constant.
 *
 * @const AutoOrderDefaultVisibleFields
 * @type {string[]}
 * @defaults {@link AutoOrderVisibleFields}
 * @description This array is intended for external use in the application. It allows iteration,
 *              manipulation, and dynamic consumption of visible field keys as basic `string[]`.
 *              The spread operator is used to copy from the immutable source to maintain type safety.
 *
 * @example
 * console.info(AutoOrderDefaultVisibleFields); // ['code', 'desc', 'maxSold', ...]
 */
export const AutoOrderDefaultVisibleFields: string[] = [...AutoOrderVisibleFields];

//#endregion
